import {
  AuthMethodsResponseSchema,
  AuthTokenResponseSchema,
  type OtpRequestBody,
  OtpRequestResponseSchema,
  type OtpVerifyBody,
  type PasswordChangeBody,
  PasswordLinkInspectResponseSchema,
  type PasswordLinkUseBody,
  type PasswordLoginBody,
} from '@clinic/contracts';
import { createQuery } from '@tanstack/svelte-query';
import { createMutation } from '../../../shared/api/mutation';
import { apiRequest } from '../../../shared/api/client';

/** Which ways in the server offers (ADR-033): a password always, an SMS code with an operator. */
export function createAuthMethods() {
  return createQuery(() => ({
    queryKey: ['auth', 'methods'],
    queryFn: ({ signal }) => apiRequest('/v1/auth/methods', { schema: AuthMethodsResponseSchema, signal }),
    staleTime: Infinity,
    retry: 1,
  }));
}

export function createPasswordLogin() {
  return createMutation(() => ({
    mutationFn: (body: PasswordLoginBody) =>
      apiRequest('/v1/auth/password/login', { method: 'POST', body, schema: AuthTokenResponseSchema }),
  }));
}

/** The token is sent in the body; it came from the URL fragment and never reaches a log. */
export function createInspectPasswordLink(token: () => string | null) {
  return createQuery(() => ({
    queryKey: ['auth', 'password-link', token()],
    enabled: token() !== null,
    queryFn: ({ signal }) =>
      apiRequest('/v1/auth/password/link/inspect', {
        method: 'POST',
        body: { token: token() },
        schema: PasswordLinkInspectResponseSchema,
        signal,
      }),
    retry: false,
    staleTime: Infinity,
  }));
}

export function createSetPasswordFromLink() {
  return createMutation(() => ({
    mutationFn: (body: PasswordLinkUseBody) =>
      apiRequest('/v1/auth/password/link/use', { method: 'POST', body, schema: AuthTokenResponseSchema }),
  }));
}

export function createChangePassword() {
  return createMutation(() => ({
    mutationFn: (body: PasswordChangeBody) => apiRequest('/v1/auth/password/change', { method: 'POST', body }),
  }));
}

export function createRequestOtp() {
  return createMutation(() => ({
    mutationFn: (body: OtpRequestBody) =>
      apiRequest('/v1/auth/otp/request', { method: 'POST', body, schema: OtpRequestResponseSchema }),
  }));
}

export function createVerifyOtp() {
  return createMutation(() => ({
    mutationFn: (body: OtpVerifyBody) =>
      apiRequest('/v1/auth/otp/verify', { method: 'POST', body, schema: AuthTokenResponseSchema }),
  }));
}
